// src/context/ThemeContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { getSettings } from '../services/settingsService';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Apex Interio',
    email: 'info@apexinterio.in',
    phone: '+91 9108602000',
    address: 'Your Address Here',
    logo: null,
    signature: null,
    favicon: null
  });
  
  const [theme, setTheme] = useState({
    primaryColor: '#E30613', // Ledger red
    accentColor: '#1A1A2E', // Ledger deep navy
    isDarkMode: false
  });
  
  const [paymentInfo, setPaymentInfo] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    accountType: 'Current Account',
    upiId: '',
    qrCode: null
  });
  
  const [materialSpecs, setMaterialSpecs] = useState({
    specifications: [],
    panelThickness: {
      backPanel: '6',
      otherPanels: '18'
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  
  // Apply theme to document
  useEffect(() => {
    // Create or get theme style element
    let themeStyleEl = document.getElementById('theme-styles');
    if (!themeStyleEl) {
      themeStyleEl = document.createElement('style');
      themeStyleEl.id = 'theme-styles';
      document.head.appendChild(themeStyleEl);
    }
    
    // Apply dark mode
    if (theme.isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Create CSS Variables
    const styles = `
      :root {
        --color-primary: ${theme.primaryColor};
        --color-primary-dark: ${adjustColor(theme.primaryColor, -20)};
        --color-primary-light: ${adjustColor(theme.primaryColor, 20)};
        --color-accent: ${theme.accentColor};
        --color-accent-dark: ${adjustColor(theme.accentColor, -20)};
        --color-accent-light: ${adjustColor(theme.accentColor, 20)};
        --color-red: #D92027;
        --color-red-dark: ${adjustColor('#D92027', -20)};
        --color-red-light: ${adjustColor('#D92027', 20)};
      }
      
      .bg-primary {
        background-color: var(--color-primary) !important;
      }
      
      .bg-primary-dark {
        background-color: var(--color-primary-dark) !important;
      }
      
      .bg-primary-light {
        background-color: var(--color-primary-light) !important;
      }
      
      .bg-accent {
        background-color: var(--color-accent) !important;
      }
      
      .bg-red {
        background-color: var(--color-red) !important;
      }
      
      .text-primary {
        color: var(--color-primary) !important;
      }
      
      .text-accent {
        color: var(--color-accent) !important;
      }
      
      .text-red {
        color: var(--color-red) !important;
      }
      
      .border-primary {
        border-color: var(--color-primary) !important;
      }
      
      .border-accent {
        border-color: var(--color-accent) !important;
      }
      
      .border-red {
        border-color: var(--color-red) !important;
      }
      
      /* Sidebar Navigation Styling */
      .sidebar-container {
        background: var(--color-bg-sidebar) !important;
        color: white !important;
      }
      
      .active-nav-item {
        background-color: var(--color-bg-sidebar-active) !important;
        color: var(--color-primary) !important;
        font-weight: 600 !important;
      }
      
      /* Override default button styles */
      button.bg-blue-600, 
      button.bg-blue-700,
      a.bg-blue-600, 
      a.bg-blue-700 {
        background-color: var(--color-primary) !important;
      }
      
      button.bg-blue-600:hover, 
      button.bg-blue-700:hover,
      a.bg-blue-600:hover, 
      a.bg-blue-700:hover {
        background-color: var(--color-primary-dark) !important;
      }
      
      button.bg-indigo-600, 
      button.bg-indigo-700,
      a.bg-indigo-600, 
      a.bg-indigo-700 {
        background-color: var(--color-accent) !important;
      }
      
      button.bg-indigo-600:hover, 
      button.bg-indigo-700:hover,
      a.bg-indigo-600:hover, 
      a.bg-indigo-700:hover {
        background-color: var(--color-accent-dark) !important;
      }

      /* Tailwind gradient utility overrides so existing blue/indigo UI adopts Ledger palette */
      .from-blue-600, .from-blue-500, .from-indigo-600 {
        --tw-gradient-from: var(--color-primary) var(--tw-gradient-from-position) !important;
        --tw-gradient-to: rgb(227 6 19 / 0) var(--tw-gradient-to-position) !important;
        --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important;
      }

      .to-indigo-600, .to-indigo-700, .to-blue-700 {
        --tw-gradient-to: var(--color-accent) var(--tw-gradient-to-position) !important;
      }

      .from-blue-100, .from-indigo-100, .to-indigo-100 {
        --tw-gradient-from: #FDE8E9 var(--tw-gradient-from-position) !important;
        --tw-gradient-to: rgb(253 232 233 / 0) var(--tw-gradient-to-position) !important;
        --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important;
      }

      /* Comprehensive text color overrides */
      .text-blue-600, .text-blue-700, .text-blue-800,
      .text-indigo-600, .text-indigo-700, .text-indigo-800,
      .text-purple-600, .text-purple-700, .text-purple-800 {
        color: var(--color-primary) !important;
      }

      /* Background color overrides for interactive elements */
      .bg-blue-50, .bg-indigo-50, .bg-purple-50 {
        background-color: #FDE8E9 !important;
      }

      .bg-blue-100, .bg-indigo-100, .bg-purple-100 {
        background-color: #FBD0D3 !important;
      }

      .bg-blue-600, .bg-blue-700,
      .bg-indigo-600, .bg-indigo-700,
      .bg-purple-600, .bg-purple-700 {
        background-color: var(--color-primary) !important;
      }

      .bg-blue-600:hover, .bg-blue-700:hover,
      .bg-indigo-600:hover, .bg-indigo-700:hover,
      .bg-purple-600:hover, .bg-purple-700:hover,
      .hover\\:bg-blue-700:hover, .hover\\:bg-blue-200:hover,
      .hover\\:bg-indigo-700:hover, .hover\\:bg-purple-700:hover,
      .hover\\:bg-purple-200:hover {
        background-color: var(--color-primary-dark) !important;
      }

      .hover\\:bg-blue-100:hover, .hover\\:bg-blue-50:hover {
        background-color: #FDE8E9 !important;
      }

      /* Border color overrides */
      .border-blue-100, .border-blue-200, .border-blue-300,
      .border-indigo-100, .border-indigo-200,
      .border-purple-100, .border-purple-200 {
        border-color: #F9B3B7 !important;
      }

      /* Ring/focus overrides */
      .ring-blue-500, .ring-indigo-500, .ring-purple-500,
      .focus\\:ring-blue-500:focus, .focus\\:ring-indigo-500:focus {
        --tw-ring-color: var(--color-primary) !important;
      }

      .focus\\:border-blue-500:focus, .focus\\:border-indigo-500:focus {
        border-color: var(--color-primary) !important;
      }
      
      /* Red theme for delete/danger actions only */
      .text-red-600, .text-red-700 {
        color: var(--color-red) !important;
      }
      
      /* Dark mode overrides */
      ${theme.isDarkMode ? `
        body, .bg-white {
          background-color: #1f2937 !important;
          color: #f3f4f6 !important;
        }
        
        .bg-gray-50, .bg-gray-100 {
          background-color: #374151 !important;
        }
        
        .text-gray-700, .text-gray-800, .text-gray-900 {
          color: #f3f4f6 !important;
        }
        
        .text-gray-500, .text-gray-600 {
          color: #d1d5db !important;
        }
        
        .border, .border-gray-200, .border-gray-300 {
          border-color: #4b5563 !important;
        }
        
        input, select, textarea {
          background-color: #374151 !important;
          color: #f3f4f6 !important;
          border-color: #4b5563 !important;
        }
        
        .shadow, .shadow-md, .shadow-lg {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.5) !important;
        }
      ` : ''}
    `;
    
    themeStyleEl.textContent = styles;
  }, [theme]);
  
  // Load settings from Firebase on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const settings = await getSettings();
        
        if (settings.companyInfo) {
          setCompanyInfo(settings.companyInfo);
        }
        
        if (settings.theme) {
          setTheme(settings.theme);
        }
        
        if (settings.paymentInfo) {
          setPaymentInfo(settings.paymentInfo);
        }
        
        if (settings.materialSpecs) {
          setMaterialSpecs(settings.materialSpecs);
        }
        
      } catch (error) {
        console.error('Error loading theme settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, []);
  
  // Update theme settings
  const updateTheme = (newTheme) => {
    setTheme(prevTheme => ({
      ...prevTheme,
      ...newTheme,
      // If updating colors, keep the companyInfo and other settings
      ...(newTheme.companyInfo ? { companyInfo: { ...prevTheme.companyInfo, ...newTheme.companyInfo } } : {}),
      ...(newTheme.paymentInfo ? { paymentInfo: { ...prevTheme.paymentInfo, ...newTheme.paymentInfo } } : {}),
      ...(newTheme.materialSpecs ? { materialSpecs: { ...prevTheme.materialSpecs, ...newTheme.materialSpecs } } : {})
    }));
  };
  
  // Update company info specifically
  const updateCompanyInfo = (newCompanyInfo) => {
    setCompanyInfo(prevInfo => ({
      ...prevInfo,
      ...newCompanyInfo
    }));
  };
  
  // Update payment info specifically
  const updatePaymentInfo = (newPaymentInfo) => {
    setPaymentInfo(prevInfo => ({
      ...prevInfo,
      ...newPaymentInfo
    }));
  };
  
  // Update material specs specifically
  const updateMaterialSpecs = (newMaterialSpecs) => {
    setMaterialSpecs(prevSpecs => ({
      ...prevSpecs,
      ...newMaterialSpecs
    }));
  };
  
  // Add a useEffect to update the favicon when it changes
  useEffect(() => {
    if (companyInfo.favicon) {
      // Update favicon
      let faviconLink = document.querySelector('link[rel="icon"]');
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }
      faviconLink.href = companyInfo.favicon;
    }
  }, [companyInfo.favicon]);
  
  return (
    <ThemeContext.Provider value={{ 
      companyInfo, 
      theme, 
      paymentInfo,
      materialSpecs,
      setTheme: updateTheme,
      setCompanyInfo: updateCompanyInfo,
      setPaymentInfo: updatePaymentInfo,
      setMaterialSpecs: updateMaterialSpecs,
      isLoading
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper function to darken or lighten a color
function adjustColor(hex, percent) {
  // Convert hex to RGB
  let r = parseInt(hex.substring(1, 3), 16);
  let g = parseInt(hex.substring(3, 5), 16);
  let b = parseInt(hex.substring(5, 7), 16);
  
  // Adjust color
  r = Math.max(0, Math.min(255, r + percent));
  g = Math.max(0, Math.min(255, g + percent));
  b = Math.max(0, Math.min(255, b + percent));
  
  // Convert RGB back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;